import { useCallback } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { ActiveActionKeyNameState, PipelineMasterSteps } from 'global-stores';
import {
	ConfigureSurvayFormState,
	CurrentStepState,
} from 'views/onboarding-flow';
import {
	ComplexConfigModalState,
	ComplexFlowUnconfigedState,
	ConfigFormModalState,
	IComplexFlowUnconfiged,
} from '../stores/states';

export const useSetComplex = () => {
	const steps = useRecoilValue(PipelineMasterSteps);
	const setActiveAction = useSetRecoilState(ActiveActionKeyNameState);
	const setCurrentStep = useSetRecoilState(CurrentStepState);
	const setIsConfigModal = useSetRecoilState(ComplexConfigModalState);
	const [complexFlowConfiged, setComplexFlowConfiged] = useRecoilState(
		ComplexFlowUnconfigedState
	);
	const setIsConfigFormModal = useSetRecoilState(ConfigFormModalState);
	const setConfigureSurvayForm = useSetRecoilState(ConfigureSurvayFormState);

	const setNextStep = useCallback(
		(pendingFlows?: IComplexFlowUnconfiged[]) => {
			const pending = pendingFlows ?? complexFlowConfiged ?? [];

			// __simplici_skip_unconfigurable_first_step__
			// Does this step actually have anything to configure? The inverse of
			// pipeline-settings-page's empty state, so the two always agree. Sign
			// Agreement renders a configurator despite carrying no metadata, and
			// dynamicForm opens the survey builder instead -- both count.
			const needsConfig = (stepId: string) => {
				if (stepId === 'dynamicForm') return true;
				const step = steps.find(el => el.key === stepId);
				const action: any = step?.actions?.[0];
				return (
					(action?.metadata?.length ?? 0) > 0 ||
					action?.key === 'signAgreementVerification'
				);
			};

			// Mark settings-free steps complete rather than merely skipping them.
			// footer-navigate blocks publishing while any flow is `!configed`, so a
			// step we skip but never complete would wedge publish behind a
			// "Please configure steps" warning pointing at an empty screen.
			//
			// The `changed` flag is load-bearing, not tidiness. `prev.map()` always
			// returns a NEW array, so writing unconditionally gives the atom a new
			// identity on every pass even when no value changed -- that re-renders
			// every subscriber, which recreates setNextStep (it depends on this
			// state), which triggers another pass. That is an infinite render loop
			// and it hard-wedges the flow builder's main thread. Returning `prev`
			// unchanged keeps the identity stable and terminates it.
			setComplexFlowConfiged(prev => {
				let changed = false;
				const next = prev.map(flow => {
					if (!flow.configed && !needsConfig(flow.stepId)) {
						changed = true;
						return { ...flow, configed: true };
					}
					return flow;
				});
				return changed ? next : prev;
			});

			// First step that genuinely needs attention; if none do, fall back to the
			// original behaviour rather than closing on an unconfigured flow.
			// __simplici_no_empty_config_screen__: the `??` fallback used to hand back
			// the first unconfigured step even when it had nothing to configure, which
			// undid the skip above. It fires whenever no pending step needs config --
			// routine now that kyc / accreditation / signAgreement are marked configed
			// on drop (they are configured on the tile), leaving only settings-free
			// steps pending. Opening on one shows "This action has no additional
			// settings" and hides the step that did need attention.
			//
			// Fall back ONLY when the catalog has not loaded: with `steps` empty
			// needsConfig() is false for everything, and closing on that would make the
			// configurator refuse to open on a slow load. Otherwise close -- the block
			// above already marked settings-free steps configed, so publish is not
			// blocked by anything we skip here.
			const pendingFlow =
				pending.find(flow => !flow.configed && needsConfig(flow.stepId)) ??
				(steps.length ? undefined : pending.find(flow => !flow.configed));
			// __simplici_next_agrees_with_configurator__: report back which flow the
			// configurator opened on, or undefined when nothing needed attention.
			// footer-navigate's Next gate reads this instead of re-deriving it, so the
			// warning toast and the configurator cannot disagree about whether there is
			// anything left to configure.
			if (!pendingFlow) {
				setIsConfigModal(false);
				return undefined;
			}
			const { id, stepId } = pendingFlow;
			if (stepId === 'dynamicForm') {
				setConfigureSurvayForm({});
				setIsConfigModal(false);
				setIsConfigFormModal(true);
			} else {
				setIsConfigFormModal(false);
				setIsConfigModal(true);
			}
			const result = steps.find(el => el.key === stepId);
			const renderObj: any = result?.actions && result.actions[0];
			setCurrentStep({ ...renderObj, id } || {});
			setActiveAction(stepId);
			return pendingFlow;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[complexFlowConfiged, steps, setComplexFlowConfiged]
	);

	return { setNextStep };
};
